/// <amd-module name="TFS/NetPromoterSurvey/Recommenders/Recommender" />

import { getCookie } from "VSS/Platform/Util/Cookie";

import {
    IRecommendation,
    RecommendationConfidence,
    RecommendationInput,
    RecommendationType
} from "TFS/NetPromoterSurvey//client/Contracts/Recommendation";
import { IInteraction } from "TFS/NetPromoterSurvey/client/Contracts/Interaction";
import { IInteractionRestClient } from "TFS/NetPromoterSurvey/client/RestClient/Interaction";
import { IRecommendationRestClient } from "TFS/NetPromoterSurvey/client/RestClient/Recommendation";
import { DataKeys } from "TFS/NetPromoterSurvey/Common";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssNavigationService } from "VSS/Platform/Navigation";

/**
 * Sets of constants used in the RestClient APIs, which couldn't be generated
 * automatically from the service code.
 */
export namespace DataConstants {
    export const EngagementId = "EngagementId";
    export const InteractionIds = "InteractionIds";
    export const AccountId = "AccountId";
    export const CollectionId = "CollectionId";
    export const ProjectId = "ProjectId";
    export const PageId = "PageId";
    export const ProductName = "ProductName";
}

export interface IRecommender {
    recommmend(): Promise<IDecision>;
    logRecommendation(): Promise<void>;
    logInteraction(data: { [key: string]: string }): Promise<void>;
}

export interface IDecision {
    isPositive: boolean;
    data?: { [key: string]: string };
}

export class Recommender implements IRecommender {
    private static readonly NetPromoterSurveyId = "NetPromoterSurvey";

    private _context: IVssPageContext;
    private _productName: string;

    constructor(context: IVssPageContext, productName: string) {
        this._context = context;
        this._productName = productName;
    }

    public recommmend(): Promise<IDecision> {
        const client: IRecommendationRestClient = this._context.getRestClient<IRecommendationRestClient>("IRecommendationRestClient");
        const inputData: { [key: string]: string } = {};

        const emptyGuid = "00000000-0000-0000-0000-000000000000";
        inputData[DataConstants.EngagementId] = Recommender.NetPromoterSurveyId;
        inputData[DataConstants.AccountId] = emptyGuid;
        inputData[DataConstants.CollectionId] = emptyGuid;
        inputData[DataConstants.ProjectId] = emptyGuid;
        inputData[DataConstants.ProductName] = this._productName;

        const input: RecommendationInput = {
            recommendationInputData: inputData,
            recommendationType: RecommendationType.Engagement
        };

        // If "TFS-NPS-DEBUG" cookie is set to "TRUE" display the survey regardless of recommendation service response
        if (getCookie("TFS-NPS-DEBUG") === "TRUE") {
            const debugRecommendationData: { [key: string]: string } = {};
            debugRecommendationData[DataKeys.DeferredTimes] = "0";
            debugRecommendationData[DataKeys.MaxAllowableDeferTimes] = "3";
            debugRecommendationData[DataKeys.NPSDebugCookieEnabled] = "TRUE";
            debugRecommendationData[DataKeys.PromptId] = "DEBUG";
            return Promise.resolve({
                isPositive: true,
                data: debugRecommendationData
            });
        }

        return new Promise<IDecision>(resolve => {
            client
                .getEngagementRecommendation(input)
                .then((recommendation: IRecommendation) => {
                    const showEngagementId: string = recommendation.recommendationData[DataConstants.EngagementId];
                    if (!!showEngagementId && showEngagementId.toLowerCase() === Recommender.NetPromoterSurveyId.toLowerCase()) {
                        resolve({
                            isPositive: true,
                            data: recommendation.recommendationData
                        });
                    }
                    resolve({ isPositive: false });
                })
                .catch(() => resolve({ isPositive: false }));
        });
    }

    public logRecommendation(): Promise<void> {
        const client: IRecommendationRestClient = this._context.getRestClient<IRecommendationRestClient>("IRecommendationRestClient");
        return client.logRecommendation(RecommendationType.PageInteraction, Recommender.NetPromoterSurveyId);
    }

    public logInteraction(data: { [key: string]: string }): Promise<void> {
        const client: IInteractionRestClient = this._context.getRestClient<IInteractionRestClient>("IInteractionRestClient");
        const interaction: IInteraction = {
            id: Recommender.NetPromoterSurveyId,
            accountId: "00000000-0000-0000-0000-000000000000",
            projectId: "00000000-0000-0000-0000-000000000000",
            timestamp: new Date(),
            interactionInputData: data
        };
        return client.add(interaction);
    }
}
